import {
  useCallback, useContext, useEffect, useMemo,
} from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import { sendTrackEvent } from '@edx/frontend-platform/analytics';

import { PostsStatusFilter } from '../../../data/constants';
import useDispatchWithState from '../../../data/hooks';
import DiscussionContext from '../../common/context';
import { selectThread } from '../../posts/data/selectors';
import { markThreadAsRead } from '../../posts/data/thunks';
import { filterPosts } from '../../utils';
import PostCommentsContext from '../postCommentsContext';
import {
  selectCommentSortOrder, selectDraftComments, selectDraftResponses,
  selectThreadComments, selectThreadCurrentPage, selectThreadHasMorePages,
} from './selectors';
import { fetchThreadComments } from './thunks';

const trackLoadMoreEvent = (postId, params) => (
  sendTrackEvent(
    'edx.forum.responses.loadMore',
    {
      postId,
      params,
    },
  )
);

export function usePost(postId) {
  const dispatch = useDispatch();
  const thread = useSelector(selectThread(postId));

  useEffect(() => {
    if (thread && !thread.read) {
      dispatch(markThreadAsRead(postId));
    }
  }, [postId]);

  return thread || {};
}

export const useShowDeletedContent = () => {
  const { learnerUsername } = useContext(DiscussionContext);
  const postFilter = useSelector(state => state.learners?.postFilter);

  // Show deleted content if we're in learner view and the deleted filter is active (contentStatus)
  return learnerUsername && postFilter?.contentStatus === PostsStatusFilter.DELETED;
};

export function usePostComments(threadType) {
  const { enableInContextSidebar, postId, learnerUsername } = useContext(DiscussionContext);
  const { includeMuted: includeMutedFromContext } = useContext(PostCommentsContext);
  const [isLoading, dispatch] = useDispatchWithState();
  const commentsSelector = useMemo(
    () => selectThreadComments(postId, learnerUsername),
    [postId, learnerUsername],
  );
  const comments = useSelector(commentsSelector);
  const reverseOrder = useSelector(selectCommentSortOrder);
  const hasMorePages = useSelector(selectThreadHasMorePages(postId));
  const currentPage = useSelector(selectThreadCurrentPage(postId));
  const showDeleted = useShowDeletedContent();

  // Check if the post author is muted by the current user
  const thread = useSelector(selectThread(postId));
  const personalMutedUsers = useSelector(state => state.learners.mutedUsers.personal);
  const courseWideMutedUsers = useSelector(state => state.learners.mutedUsers.course);
  const isAuthorMuted = thread?.author
    ? (personalMutedUsers.includes(thread.author) || courseWideMutedUsers.includes(thread.author))
    : false;

  // Include muted content if explicitly requested from context or if author is muted
  const shouldIncludeMuted = includeMutedFromContext || isAuthorMuted;

  const endorsedCommentsIds = useMemo(() => (
    [...filterPosts(comments, 'endorsed')].map(comment => comment.id)
  ), [comments]);

  const unEndorsedCommentsIds = useMemo(() => (
    [...filterPosts(comments, 'unendorsed')].map(comment => comment.id)
  ), [comments]);

  const handleLoadMoreResponses = useCallback(async () => {
    const params = {
      threadType,
      page: currentPage + 1,
      reverseOrder,
      includeMuted: shouldIncludeMuted,
      showDeleted,
    };
    await dispatch(fetchThreadComments(postId, params));
    trackLoadMoreEvent(postId, params);
  }, [currentPage, threadType, postId, reverseOrder, showDeleted, shouldIncludeMuted]);

  useEffect(() => {
    const abortController = new AbortController();

    dispatch(fetchThreadComments(postId, {
      threadType,
      page: 1,
      reverseOrder,
      enableInContextSidebar,
      showDeleted,
      signal: abortController.signal,
      includeMuted: shouldIncludeMuted,
    }));

    return () => {
      abortController.abort();
    };
  }, [postId, threadType, reverseOrder, enableInContextSidebar, showDeleted, shouldIncludeMuted]);

  return {
    endorsedCommentsIds,
    unEndorsedCommentsIds,
    hasMorePages,
    isLoading,
    handleLoadMoreResponses,
  };
}

export function useCommentsCount(postId) {
  const { learnerUsername } = useContext(DiscussionContext);
  const commentsSelector = useMemo(
    () => selectThreadComments(postId, learnerUsername),
    [postId, learnerUsername],
  );
  return useSelector(commentsSelector).length;
}

export const useDraftContent = () => {
  const comments = useSelector(selectDraftComments);
  const responses = useSelector(selectDraftResponses);

  const getObjectByParentId = (data, parentId, isComment, id) => Object.values(data)
    .find(draft => (isComment ? draft.parentId === parentId && (id ? draft.id === id : draft.isNewContent === true)
      : draft.threadId === parentId && (id ? draft.id === id : draft.isNewContent === true)));

  const updateDraftData = (draftData, newDraftObject) => ({
    ...draftData,
    [newDraftObject.id]: newDraftObject,
  });

  const addDraftContent = (content, parentId, id, threadId) => {
    const data = parentId ? comments : responses;
    const draftParentId = parentId || threadId;
    const isComment = !!parentId;
    const existingObj = getObjectByParentId(data, draftParentId, isComment, id);
    const newObject = existingObj
      ? { ...existingObj, content }
      : {
        threadId,
        content,
        parentId,
        id: id || uuidv4(),
        isNewContent: !id,
      };

    const updatedComments = parentId ? updateDraftData(comments, newObject) : comments;
    const updatedResponses = !parentId ? updateDraftData(responses, newObject) : responses;

    return { updatedComments, updatedResponses };
  };

  const getDraftContent = (parentId, threadId, id) => {
    if (id) {
      return parentId ? comments?.[id]?.content : responses?.[id]?.content;
    }

    const data = parentId ? comments : responses;
    const draftParentId = parentId || threadId;
    const isComment = !!parentId;

    return getObjectByParentId(data, draftParentId, isComment, id)?.content;
  };

  const removeItem = (draftData, objId) => {
    /* eslint-disable-next-line @typescript-eslint/naming-convention */
    const { [objId]: _, ...newDraftData } = draftData;
    return newDraftData;
  };

  const updateContent = (items, itemId, parentId, isComment) => {
    const itemObj = itemId ? items[itemId] : getObjectByParentId(items, parentId, isComment, itemId);
    return itemObj ? removeItem(items, itemObj.id) : items;
  };

  const removeDraftContent = (parentId, id, threadId) => {
    const updatedResponses = !parentId ? updateContent(responses, id, threadId, false) : responses;
    const updatedComments = parentId ? updateContent(comments, id, parentId, true) : comments;

    return { updatedResponses, updatedComments };
  };

  return {
    addDraftContent,
    getDraftContent,
    removeDraftContent,
  };
};
